import React, { useState } from "react";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormLabel,
    FormHelperText,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { Add, Edit } from '@mui/icons-material';
import { updateLead } from "../../redux/Slice/LeadSlice";
import { setLoading } from "../../redux/Slice/loaderSlice";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const AddDemoSchedule = ({ data, setData }) => {
    const [isEditingDemoSchedule, setIsEditingDemoSchedule] = useState(false);
    const [editingDemoScheduleId, setEditingDemoScheduleId] = useState(null);

    const dispatch = useDispatch();
    const params = useParams();

    const handleAddDemoSchedule = () => {
        setEditingDemoScheduleId(null);
        setIsEditingDemoSchedule(true);
    };

    const handleEditDemoSchedule = (id) => {
        const demoSchedule = data?.demo_schedule?.find((demo) => demo._id === id);
        if (demoSchedule) {
            setEditingDemoScheduleId(id);
            setIsEditingDemoSchedule(true);
        }
    };

    const handleSaveDemoSchedule = async (values) => {
        if (!values.demo_products || !values.demo_notes || !values.demo_dateAndTime || !values.demo_location || !values.demo_status) {
            alert("Please fill in all fields.");
            return;
        }
        
        dispatch(setLoading(true));
        
        const updatedDemoSchedule = data.demo_schedule.map((demo) =>
            demo._id === editingDemoScheduleId
                ? { ...demo, ...values }
                : demo
        );

        if (!editingDemoScheduleId) {
            updatedDemoSchedule.push({ ...values });
        }

        await dispatch(
            updateLead({
                l_id: params.id,
                formData: { ...data, demo_schedule: updatedDemoSchedule },
            })
        );
        setIsEditingDemoSchedule(false);
        dispatch(setLoading(false));
    };

    const validationSchema = Yup.object({
        demo_products: Yup.string().required("Demo Products is required"),
        demo_notes: Yup.string().required("Demo Notes is required"),
        demo_dateAndTime: Yup.string().required("Demo Date and Time are required"),
        demo_location: Yup.string().required("Demo Location is required"),
        demo_status: Yup.string().required("Demo Status is required"),
    });

    return (
        <>
            <Grid item size={12}>
               <Paper elevation={3} sx={{ p: 2, pb: 5 }}>
                    <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        display="flex"
                    >
                        <Typography variant="h6">Demo Schedule</Typography>
                        <div>
                            <IconButton onClick={handleAddDemoSchedule}>
                                <Add />
                            </IconButton>
                        </div>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Demo Products</TableCell>
                                    <TableCell>Demo Notes</TableCell>
                                    <TableCell>Demo Date & Time</TableCell>
                                    <TableCell>Demo Location</TableCell>
                                    <TableCell>Demo Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.demo_schedule?.map((demo) => (
                                    <TableRow key={demo._id}>
                                        <TableCell>{demo.demo_products}</TableCell>
                                        <TableCell>{demo.demo_notes}</TableCell>
                                        <TableCell>{dayjs(demo.demo_dateAndTime).format('DD MMM ddd YYYY : hh.mm A')}</TableCell>
                                        <TableCell>{demo.demo_location}</TableCell>
                                        <TableCell>{demo.demo_status}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() =>
                                                    handleEditDemoSchedule(demo._id)
                                                }
                                            >
                                                <Edit />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
            <Dialog open={isEditingDemoSchedule} onClose={() => setIsEditingDemoSchedule(false)}>
                <DialogTitle>
                    {editingDemoScheduleId ? "Edit Demo Schedule" : "Add Demo Schedule"}
                </DialogTitle>
                <DialogContent>
                    <Formik
                        initialValues={{
                            demo_products: "",
                            demo_notes: "",
                            demo_dateAndTime: "",
                            demo_location: "",
                            demo_status: "",
                            ...data?.demo_schedule?.find(
                                (demo) => demo._id === editingDemoScheduleId
                            ),
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSaveDemoSchedule}
                    >
                        {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                            <Form>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Demo Products</FormLabel>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="demo_products"
                                        placeholder="Demo Products"
                                        value={values.demo_products}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.demo_products && Boolean(errors.demo_products)}
                                        helperText={touched.demo_products && errors.demo_products}
                                    />
                                </FormControl>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Demo Notes</FormLabel>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="demo_notes"
                                        placeholder="Demo Notes"
                                        value={values.demo_notes}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.demo_notes && Boolean(errors.demo_notes)}
                                        helperText={touched.demo_notes && errors.demo_notes}
                                    />
                                </FormControl>
                                <Grid container spacing={4} justifyContent="space-between">
                                    <Grid item xs={6}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <FormControl fullWidth sx={{ mb: 2 }}>
                                                <FormLabel>Demo Date</FormLabel>
                                                <MobileDateTimePicker
                                                    value={values.demo_dateAndTime ? dayjs(values.demo_dateAndTime) : null}
                                                    onChange={(newValue) => {
                                                        if (newValue && newValue.isValid()) {
                                                            setFieldValue("demo_dateAndTime", newValue);
                                                        } else {
                                                            setFieldValue("demo_dateAndTime", null);
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            fullWidth
                                                            error={touched.demo_dateAndTime && Boolean(errors.demo_dateAndTime)}
                                                            helperText={touched.demo_dateAndTime && errors.demo_dateAndTime}
                                                        />
                                                    )}
                                                />
                                            </FormControl>
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <FormLabel>Demo Location</FormLabel>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        name="demo_location"
                                        placeholder="Demo Location"
                                        value={values.demo_location}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.demo_location && Boolean(errors.demo_location)}
                                        helperText={touched.demo_location && errors.demo_location}
                                    />
                                </FormControl>

                               <FormControl fullWidth sx={{ mb: 2 }}>
                                 <FormLabel>Demo Status</FormLabel>
                                 <Select
                                     name="demo_status"
                                     value={values.demo_status}
                                     onChange={handleChange}
                                     onBlur={handleBlur}
                                     displayEmpty
                                 >
                                     <MenuItem value="" disabled>Demo Status</MenuItem>
                                     <MenuItem value="Scheduled">Scheduled</MenuItem>
                                     <MenuItem value="Completed">Completed</MenuItem>
                                     <MenuItem value="Cancelled">Cancelled</MenuItem>
                                 </Select>
                                 <FormHelperText error={Boolean(errors.demo_status)}>
                                     {errors.demo_status}
                                 </FormHelperText>
                                </FormControl>

                                <DialogActions>
                                    <Button onClick={() => setIsEditingDemoSchedule(false)} color="primary">
                                        Cancel
                                    </Button>
                                    <Button type="submit" color="primary">
                                        Save
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AddDemoSchedule;
